import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
} from "react";

import { IDocumentTypeEnum } from "@/models/doctor";
import { CHAT_LIST_TYPE } from "@/models/chat";
import { IUploadDocumentEnums } from "@/models/permission";
import { IModalKeyEnums } from "@/models/permission";

import useFirebaseMessaging from "@/utils/hooks/useFirebaseMessaging";
import { uploadFileToS3Api } from "@/utils/apis/doctor/prescription";
import { notify } from "@/utils/toaster";
import { INotifyEnum } from "@/utils/toaster";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

// Create Context
const FirebaseMessagingContext = createContext<any>(null);

// Provider Component
export const FirebaseMessagingProvider = ({ children }: PropsWithChildren) => {
  const {
    listType,
    channelList,
    enrichedChannelList,
    handleChannel,
    messages,
    patientChatMediaList,
    channelId,
    supportChat,
    handleSendMessage,
    markMessageAsRead,
    markAllMessagesAsRead,
    handleList,
    removeMemberFromChat,
    updateChatStatusOrFavorite,
    handleDocumentId,
    paginatedDocs,
    isLoadingMore,
    hasMoreDocs,
    fetchPaginatedConversations,
    setSwitchChatTab,
    fetchConversationById,
    globalUnreadCounts,
    isLoadingMessages,
  } = useFirebaseMessaging({
    chatListType: CHAT_LIST_TYPE.PATIENT,
  });

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const permissionRestrictionHandler = usePermissionRestriction();

  const handleFileUpload = async (formData: FormData) => {
    const isUploadToS3PermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.UPLOAD_DOCUMENT,
      IUploadDocumentEnums.UPLOAD_TO_S3
    );
    if (!isUploadToS3PermissionGranted) return;

    setIsUploading(true);
    try {
      const uploadFileToS3Response = await uploadFileToS3Api(
        IDocumentTypeEnum.CHAT,
        formData
      );
      if ("result" in uploadFileToS3Response) {
        const { thumbnailUrl, url } = uploadFileToS3Response.result;
        return { thumbnail: thumbnailUrl, url };
      } else {
        notify(INotifyEnum.ERROR, uploadFileToS3Response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <FirebaseMessagingContext.Provider
      value={{
        messages,
        patientChatMediaList,
        channelList,
        enrichedChannelList,
        isUploading,
        listType,
        channelId,
        supportChat,
        handleFileUpload,
        handleChannel,
        handleSendMessage,
        markMessageAsRead,
        markAllMessagesAsRead,
        handleList,
        removeMemberFromChat,
        updateChatStatusOrFavorite,
        handleDocumentId,
        paginatedDocs,
        isLoadingMore,
        hasMoreDocs,
        fetchPaginatedConversations,
        setSwitchChatTab,
        fetchConversationById,
        globalUnreadCounts,
        isLoadingMessages,
      }}
    >
      {children}
    </FirebaseMessagingContext.Provider>
  );
};

// Custom Hook for Consuming Context
export const useFirebaseMessagingContext = () => {
  const context = useContext(FirebaseMessagingContext);
  if (!context) {
    throw new Error(
      "useFirebaseMessagingContext must be used within a FirebaseMessagingProvider"
    );
  }
  return context;
};
